import React, { useEffect, useMemo, useState, useCallback } from "react"
import Icon from "../../../components/AppIcon"
import { Card, CardContent } from "../../../components/ui/card"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../components/ui/table"
import { listPrograms } from "../../../utils/programHelpers"
import { listCourses } from "../../../utils/courseHelpers"
import { computeProgramPerformance } from "../../../utils/programPerformanceHelpers"
import {
  getProgramCohorts,
  computeProgramAssessmentPerformance,
  computeSingleProgramAnalytics,
} from "../../../utils/programDetailHelpers"

const formatCurrency = (n) => `$${(n || 0).toLocaleString()}`

const IntelligenceProgramsPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [programs, setPrograms] = useState([])
  const [courses, setCourses] = useState([])
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)

  const [selectedProgramId, setSelectedProgramId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [cohorts, setCohorts] = useState([])
  const [assessment, setAssessment] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => {
    if (!orgId) return
    const load = async () => {
      setLoading(true)
      const [{ programs: progList }, { courses: courseList }, perfRows] = await Promise.all([
        listPrograms(orgId),
        listCourses(orgId),
        computeProgramPerformance(orgId),
      ])
      setPrograms(progList || [])
      setCourses(courseList || [])
      setRows(perfRows || [])
      setLoading(false)
    }
    load()
  }, [orgId])

  const openProgram = useCallback(
    async (program) => {
      setSelectedProgramId(program.programId)
      setDetailLoading(true)
      const courseIds = program?.courseIds || []
      const [analytics, cohortRows, perf] = await Promise.all([
        computeSingleProgramAnalytics(orgId, program, courses),
        getProgramCohorts(orgId, courseIds),
        computeProgramAssessmentPerformance(orgId, courseIds),
      ])
      setDetail(analytics)
      setCohorts(cohortRows || [])
      setAssessment(perf)
      setDetailLoading(false)
    },
    [orgId, courses]
  )

  const selectedProgram = programs.find((p) => p.programId === selectedProgramId) || null

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground text-sm">Loading program intelligence...</div>
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground max-w-xl">
        How each program performs across its bundled courses — cohorts, assessments, and outcomes
        in one view.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
        <Card className="p-0 dark:bg-transparent overflow-x-auto">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">Program</TableHead>
                  <TableHead className="text-right">Learners</TableHead>
                  <TableHead className="text-right">Progress</TableHead>
                  <TableHead className="text-right">Completion</TableHead>
                  <TableHead className="text-right pr-5">Credentials</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const program = programs.find((p) => p.programId === row.id)
                  return (
                    <TableRow
                      key={row.id}
                      className={`cursor-pointer hover:bg-muted/50 ${
                        selectedProgramId === row.id ? "bg-primary/5" : ""
                      }`}
                      onClick={() => program && openProgram(program)}
                    >
                      <TableCell className="pl-5 max-w-[220px]">
                        <p className="font-medium truncate">{row.name}</p>
                        <p className="text-xs text-muted-foreground">{row.type}</p>
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">{row.learners}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{row.progress}%</TableCell>
                      <TableCell className="text-right text-muted-foreground">{row.completion}%</TableCell>
                      <TableCell className="text-right pr-5 text-muted-foreground">{row.credentials}</TableCell>
                    </TableRow>
                  )
                })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No active programs yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div>
          {!selectedProgram ? (
            <Card className="p-8 text-center dark:bg-transparent h-full flex flex-col items-center justify-center">
              <Icon name="Layers" size={26} className="text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Select a program to see cohorts and assessment data.</p>
            </Card>
          ) : detailLoading ? (
            <Card className="p-8 text-center dark:bg-transparent">
              <p className="text-sm text-muted-foreground">Loading {selectedProgram.title}...</p>
            </Card>
          ) : (
            <div className="space-y-4">
              <Card className="dark:bg-transparent">
                <CardContent className="p-4 space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Program</p>
                    <h3 className="font-semibold text-foreground">{selectedProgram.title}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Learners</p>
                      <p className="text-lg font-bold text-foreground">{detail?.totalLearners ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Completion Rate</p>
                      <p className="text-lg font-bold text-foreground">{detail?.completionRate ?? 0}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Credentials Issued</p>
                      <p className="text-lg font-bold text-foreground">{detail?.credentialsIssued ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Revenue</p>
                      <p className="text-lg font-bold text-foreground">{formatCurrency(detail?.revenue)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="dark:bg-transparent">
                <CardContent className="p-4 space-y-2">
                  <h4 className="font-semibold text-sm text-foreground">Assessment Performance</h4>
                  {assessment?.totalCompleted ? (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">Avg. Score</p>
                        <p className="font-semibold text-foreground">{assessment.avgScore}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Pass Rate</p>
                        <p className="font-semibold text-foreground">{assessment.passRate}%</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">No assessment attempts yet.</p>
                  )}
                </CardContent>
              </Card>

              <Card className="dark:bg-transparent">
                <CardContent className="p-4 space-y-2">
                  <h4 className="font-semibold text-sm text-foreground">Cohort Comparison</h4>
                  {cohorts.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No cohorts assigned in this program yet.</p>
                  ) : (
                    cohorts.map((c) => (
                      <div key={c.name} className="flex items-center justify-between py-1.5 text-sm">
                        <span className="text-foreground truncate">{c.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {c.learners} learners · {c.avgProgress}% avg progress
                        </span>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default IntelligenceProgramsPanel